import { motion } from "motion/react";
import { ArrowUpRight, Crown, Flame, MousePointerClick, Pin, Sparkles, TrendingUp, Zap } from "lucide-react";
import { badgesFor, formatIDR, levelFor } from "@/lib/board-constants";
import { ListingThumb } from "@/components/listing-thumb";
import { ShareListingButton } from "@/components/share-buttons";
import { Skeleton } from "@/components/ui/skeleton";

export type BoardListing = {
  id: string;
  slug: string;
  name: string;
  url: string;
  description: string;
  category: string;
  total_amount: number;
  clicks: number;
  points: number;
  highlight_until: string | null;
  pin_until: string | null;
  boost_until: string | null;
  created_at: string;
};

const badgeIcon: Record<string, typeof Pin> = {
  Pin,
  Boost: Flame,
  Sorotan: Sparkles,
  Populer: TrendingUp,
};

/** Baris papan peringkat gaya kanan-kiri: rank + logo + info di kiri, nilai + aksi di kanan. */
export function ListingCard({
  listing,
  rank,
  onSelect,
  onOutbid,
}: {
  listing: BoardListing;
  rank: number;
  onSelect: (listing: BoardListing) => void;
  onOutbid: (listing: BoardListing) => void;
}) {
  const badges = badgesFor(listing);
  const level = levelFor(listing.points);
  const isTop = rank === 1;
  const isPodium = rank <= 3;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -2 }}
      className="group relative"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(listing)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(listing);
          }
        }}
        className={`surface-card relative grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-3 overflow-hidden p-3 transition-all duration-300 outline-none hover:border-primary/50 hover:shadow-[var(--shadow-elegant,0_10px_30px_-16px_rgb(0_0_0/0.35))] focus-visible:ring-2 focus-visible:ring-primary/50 sm:p-4 ${
          isTop ? "ring-1 ring-primary/50" : isPodium ? "ring-1 ring-primary/20" : ""
        }`}
      >
        {isPodium && (
          <span className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-[var(--gradient-accent,var(--color-primary))] opacity-80" />
        )}
        <span className="pointer-events-none absolute inset-0 -translate-x-full bg-linear-to-r from-transparent via-primary/5 to-transparent transition-transform duration-700 group-hover:translate-x-full" />

        {/* KIRI */}
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex w-9 shrink-0 flex-col items-center">
            <span
              className={`font-display text-lg leading-none font-semibold tabular-nums sm:text-xl ${
                isTop ? "text-primary" : isPodium ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {rank}
            </span>
            {isTop ? (
              <Crown className="mt-1 size-3.5 text-primary" />
            ) : (
              <span className="mt-1 text-[10px] text-muted-foreground">Lv{level.level}</span>
            )}
          </div>

          <ListingThumb url={listing.url} name={listing.name} size={40} />

          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="truncate font-display text-sm font-semibold sm:text-base">{listing.name}</span>
              <ArrowUpRight className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
            </div>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{listing.description || listing.url}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="rounded-md bg-secondary px-1.5 py-0.5">{listing.category}</span>
              <span className="inline-flex items-center gap-1">
                <MousePointerClick className="size-3" />
                {listing.clicks}
              </span>
              <span className="inline-flex items-center gap-1">
                <Zap className="size-3" />
                {listing.points}
              </span>
              {badges.slice(0, 2).map((badge) => {
                const Icon = badgeIcon[badge];
                return (
                  <span
                    key={badge}
                    className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-1.5 py-0.5 font-medium text-accent-foreground"
                  >
                    {Icon ? <Icon className="size-2.5" /> : null}
                    {badge}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* KANAN */}
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span
            className={`font-display text-sm font-semibold tabular-nums sm:text-base ${
              isTop ? "text-primary" : ""
            }`}
          >
            {listing.total_amount > 0 ? formatIDR(listing.total_amount) : "GRATIS"}
          </span>
          <div className="flex items-center gap-1.5">
            <div onClick={(e) => e.stopPropagation()}>
              <ShareListingButton
                slug={listing.slug}
                name={listing.name}
                rank={rank}
                size="icon"
                variant="outline"
                showLabel={false}
              />
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOutbid(listing);
              }}
              className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-[11px] font-medium transition-colors hover:border-primary hover:bg-primary-soft hover:text-accent-foreground"
            >
              Rebut
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function ListingRowSkeleton() {
  return (
    <div className="surface-card grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 p-3 sm:p-4">
      <div className="flex min-w-0 items-center gap-3">
        <Skeleton className="h-5 w-6 rounded-md" />
        <Skeleton className="size-10 rounded-xl" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-3.5 w-40 max-w-full" />
          <Skeleton className="h-3 w-56 max-w-full" />
          <Skeleton className="h-3 w-28" />
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-6 w-14 rounded-lg" />
      </div>
    </div>
  );
}
